import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, Inject, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import type { I18NextLoadResult, ITranslationService } from 'angular-i18next';
import { defaultInterpolationFormat, I18NextModule, I18NEXT_SERVICE } from 'angular-i18next';
import '@angular/compiler';

//import { FadvComponentsModule } from '@fa-switch-sharedservices/ng-elements';

// used to create fake backend
import { fakeBackendProvider } from './_helpers';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  FadvAccordionModule,
  FadvAlertModule,
  FadvApplicationModule,
  FadvAvatarModule,
  FadvBadgeModule,
  FadvBoxModule,
  FadvButtonGroupModule,
  FadvButtonModule,
  FadvCalendarModule,
  FadvCardModule,
  FadvCheckboxCardModule,
  FadvCheckboxModule,
  FadvStepperModule,
  FadvDashboardModule,
  FadvDatePickerModule,
  FadvDetailsModule,
  FadvDrawerModule,
  FadvDropdownModule,
  FadvExpansionModule,
  FadvFilterModule,
  FadvGridModule,
  FadvHighlightModule,
  FadvIconModule,
  FadvInputModule,
  FadvLoaderModule,
  FadvLayoutModule,
  FadvMessageBoxModule,
  FadvModalModule,
  FadvPaginationModule,
  FadvProgressBarModule,
  FadvProgressCardModule,
  FadvProgressDotsModule,
  FadvProgressRingModule,
  FadvRadioCardModule,
  FadvRadioGroupModule,
  FadvRadioModule,
  FadvRangeModule,
  FadvRatingModule,
  FadvSelectModule,
  FadvSkeletonModule,
  FadvSpinnerModule,
  FadvSwitchModule,
  FadvTableModule,
  FadvTabsModule,
  FadvTagModule,
  FadvTextareaModule,
  FadvTooltipModule,
  FadvTypographyModule,
  FadvUploaderModule,
  FadvImageComparerModule,
  FadvInlineAlertModule,
  FadvToastModule,
  FadvToastOutletModule,
  FadvPasswordValidatorModule,
} from '@fa-switch-sharedservices/ng-elements';

import { environment } from '../environments/environment';
import { InsightsModule } from './insights.module';
import { AppRoutingModule } from './app-routing.module';
import { JwtInterceptor, ErrorInterceptor, appInitializer } from './_helpers';
import { AccountService } from './_services';
import { AppComponent } from './app.component';
import { AlertComponent } from './_components';
import { SharedModule } from './shared/shared.module';
import { CampaignModule } from './campaign/campaign.module';
import { PartnerModule } from './partner/partner.module';
import { PdfModule } from './pdftemplate/pdftemplate.module';
import { ForwardComponent } from './forward/forward.component';


export function appInit(i18next: ITranslationService): () => Promise<I18NextLoadResult> {
  return () =>
    i18next
      .use(LanguageDetector)
      .use(HttpApi)
      .init({
        supportedLngs: ['en'],
        fallbackLng: 'en',
        debug: true,
        returnEmptyString: false,
        ns: ['components'],
        interpolation: {
          format: I18NextModule.interpolationFormat(defaultInterpolationFormat),
        },
        backend: {
          loadPath: '/assets/locales/{{lng}}/{{ns}}.json',
        },
        detection: {
          order: ['querystring', 'cookie'],
          lookupCookie: 'lang',
          lookupQuerystring: 'lng',
          caches: ['localStorage', 'cookie'],
          cookieMinutes: 1440,
        },
      });
}

export function localeIdFactory(i18next: ITranslationService): string {
  return i18next.language;
}

export const I18N_PROVIDERS = [
  {
    provide: APP_INITIALIZER,
    useFactory: appInit,
    deps: [I18NEXT_SERVICE],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    deps: [I18NEXT_SERVICE],
    useFactory: localeIdFactory,
  },
];

@NgModule({
  imports: [
    InsightsModule,
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    EffectsModule.forRoot([]),
    FadvAccordionModule,
    FadvAlertModule,
    FadvApplicationModule,
    FadvAvatarModule,
    FadvBadgeModule,
    FadvBoxModule,
    FadvButtonGroupModule,
    FadvButtonModule,
    FadvCalendarModule,
    FadvCardModule,
    FadvCheckboxCardModule,
    FadvCheckboxModule,
    FadvDashboardModule,
    FadvDatePickerModule,
    FadvStepperModule,
    FadvDetailsModule,
    FadvDrawerModule,
    FadvDropdownModule,
    FadvExpansionModule,
    FadvFilterModule,
    FadvGridModule,
    FadvHighlightModule,
    FadvIconModule,
    FadvInlineAlertModule,
    FadvInputModule,
    FadvLoaderModule,
    FadvLayoutModule,
    FadvMessageBoxModule,
    FadvModalModule,
    FadvPaginationModule,
    FadvProgressBarModule,
    FadvProgressCardModule,
    FadvProgressDotsModule,
    FadvProgressRingModule,
    FadvRadioCardModule,
    FadvRadioGroupModule,
    FadvRadioModule,
    FadvRangeModule,
    FadvRatingModule,
    FadvSelectModule,
    FadvSkeletonModule,
    FadvSpinnerModule,
    FadvSwitchModule,
    FadvTableModule,
    FadvTabsModule,
    FadvTagModule,
    FadvTextareaModule,
    FadvTooltipModule,
    FadvTypographyModule,
    FadvUploaderModule,
    FormsModule,
    FadvImageComparerModule,
    FadvToastModule,
    FadvToastOutletModule,
    FadvPasswordValidatorModule,
    ReactiveFormsModule,
    SharedModule,
    I18NextModule,
    CampaignModule,
    PartnerModule,
    PdfModule,
    I18NextModule.forRoot(),

    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: true,
          strictActionSerializability: true,
          strictActionWithinNgZone: true,
          strictActionTypeUniqueness: true,
        },
      }
    ),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    }),
  ],
  declarations: [
    AppComponent,
    AlertComponent,
    ForwardComponent
  ],
  providers: [I18N_PROVIDERS,
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AccountService] },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },

    // provider used to create fake backend
    // fakeBackendProvider
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
